import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { AuthContext } from '../context/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { notificationsCopy, pickPageCopy } from '../i18n/pageCopy';
import { localizeReaderText } from '../i18n/readerTerminology';
import { getLocalizedError } from '../i18n/errorCopy';
import {
  formatNotificationTime,
  isNotificationPermissionError,
  markAllNotificationsRead,
  markNotificationRead,
  subscribeNotifications,
} from '../services/notifications';
import '../style/Marketplace.css';

const NotificationsPage = () => {
  const { user, userType } = useContext(AuthContext);
  const { language } = useI18n();
  const copy = pickPageCopy(language, notificationsCopy);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const dashboardPath = userType === 'admin'
    ? '/admin-login'
    : userType === 'teller'
      ? '/teller-dashboard'
      : '/user-dashboard';

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return undefined;
    }

    if (!userType) return undefined;

    setAccessDenied(false);
    return subscribeNotifications(
      user.uid,
      userType,
      (rows) => {
        setNotifications(rows);
        setLoading(false);
      },
      (notificationError) => {
        if (isNotificationPermissionError(notificationError)) {
          setAccessDenied(true);
          setError('');
          setNotifications([]);
          setLoading(false);
          return;
        }
        setError(getLocalizedError(notificationError, language, copy.loadError));
        setLoading(false);
      },
    );
  }, [copy.loadError, language, navigate, user, userType]);

  useEffect(() => {
    if (accessDenied) navigate(dashboardPath, { replace: true });
  }, [accessDenied, dashboardPath, navigate]);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );
  const handleRead = async (notification) => {
    if (notification.read) return;
    try {
      await markNotificationRead(notification.id);
    } catch (readError) {
      if (isNotificationPermissionError(readError)) {
        setAccessDenied(true);
        return;
      }
      setError(getLocalizedError(readError, language, copy.updateError));
    }
  };

  const handleReadAll = async () => {
    setSaving(true);
    setError('');
    try {
      await markAllNotificationsRead(notifications);
    } catch (readError) {
      if (isNotificationPermissionError(readError)) {
        setAccessDenied(true);
        return;
      }
      setError(getLocalizedError(readError, language, copy.updatesError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="market-page notifications-page">
        <div className="notifications-page-inner">
          <section className="dashboard-hero notification-hero">
            <div>
              <p className="eyebrow">{copy.eyebrow}</p>
              <h1>{copy.title}</h1>
              <p>{copy.body}</p>
            </div>
            <div className="notification-hero-actions">
              <strong>{unreadCount}</strong>
              <span>{copy.unread}</span>
              <button type="button" onClick={handleReadAll} disabled={saving || unreadCount === 0}>
                {saving ? copy.updating : copy.markAll}
              </button>
            </div>
          </section>

          {error && <div className="market-alert" role="alert">{error}</div>}

          <section className="dashboard-card notification-center-card">
            <div className="section-title-row">
              <h2>{copy.heading}</h2>
              <Link to={dashboardPath}>{copy.back}</Link>
            </div>

            {loading ? (
              <div className="empty-card">{copy.loading}</div>
            ) : notifications.length === 0 ? (
              <div className="empty-card">
                <strong>{copy.empty}</strong>
                <span>{copy.emptyBody}</span>
              </div>
            ) : (
              <div className="notification-list">
                {notifications.map((notification) => (
                  <Link
                    key={notification.id}
                    to={notification.actionUrl || dashboardPath}
                    className={`notification-item ${notification.read ? '' : 'unread'}`}
                    onClick={() => handleRead(notification)}
                  >
                    <span className="notification-symbol" aria-hidden="true">
                      {notification.type === 'booking_pending_payment' ? '€' : '✦'}
                    </span>
                    <span className="notification-copy">
                      <strong>{localizeReaderText(notification.title || copy.update, language)}</strong>
                      <span>{localizeReaderText(notification.body || '', language)}</span>
                      <small>{formatNotificationTime(notification)}</small>
                    </span>
                    {!notification.read && <i aria-label={copy.unreadLabel} />}
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NotificationsPage;
